import React from 'react';
import css from './ItemForSelect.module.css';
import { ItemEntity } from '@entities/Item/Types';
import { ItemTypeEntity } from '@entities/ItemType/Types';
import { notifyErrorCustom } from '@shared/Widgets/ToastNotifications/ToastNotifications';
import CustomNumberInput from '@shared/Ui/CustomInputs/NumberInput/NumberInput';
import BasicButton from '@shared/Ui/Buttons/BasicButton/BasicButton';
import { ItemAvailability } from '@modules/Steps/Shared/Widgets/Modals/ItemsSelectionModal/ItemForSelect/ItemAvailability/ItemAvailability';
import { GetImageFromGalleries } from '@modules/Steps/Shared/Helpers/GetImageFromGalleries';
import { DEFAULT_ITEM_IMAGE } from '@shared/Constants/Images/DefaultItemImage';
import { SelectItemsModalElementsIdsEnum } from '@modules/Steps/Shared/Enums/SelectItemsModalElementsIdsEnum';
import { useProjectStore } from '../../../../../../../Stores/ProjectStore';
import classNames from 'classnames';
import { GetPackagingText } from '@viewModels/ItemTypeViewModel/Services/GetPackagingText';
import { ItemTypeViewModel } from '@viewModels/ItemTypeViewModel/ItemTypeViewModel';
import { FormatNumber } from '@shared/Helpers/FormatValue/FormatNumber/FormatNumber';

interface ItemForSelectProps {
    item: ItemEntity;
    isFillDisabled: boolean;
    itemType: ItemTypeEntity;
}

export const ItemForSelect: React.FC<ItemForSelectProps> = ({
    item,
    isFillDisabled,
    itemType,
}) => {
    const { qty, qtyFromStore, id, packaging, merchantInfo } = item;

    const { customProject } = useProjectStore();

    const itemTypeViewModel = new ItemTypeViewModel();

    const quantity = qty ?? 0;
    const isDisabled = typeof qtyFromStore === 'number' && qtyFromStore <= 0;
    const isDecreaseDisabled = isDisabled || quantity < 1;
    const isIncreaseDisabled = isDisabled || quantity >= qtyFromStore;

    const imgUrl =
        item?.merchantInfo?.image ??
        GetImageFromGalleries(DEFAULT_ITEM_IMAGE, item.galleries);

    const handleShowItem = () => {
        if (customProject.storeId) {
            window.open(
                `${merchantInfo?.link}?store=S${customProject.storeId}`
            );
        }
    };

    const handleIncreaseQuantity = () => {
        if (!Number.isNaN(qtyFromStore) || quantity <= qtyFromStore) {
            itemTypeViewModel.updateItemQuantity(id, quantity + 1);
        }
    };

    const handleDecreaseQuantity = () => {
        if (quantity >= 1) {
            itemTypeViewModel.updateItemQuantity(id, quantity - 1);
        }
    };

    const handleChangeQuantity = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;

        if (isNaN(Number(value))) {
            return;
        }

        if (Number(value) > qtyFromStore && typeof qtyFromStore === 'number') {
            notifyErrorCustom(`Maximum allowed quantity ${qtyFromStore}`);
            return;
        }

        itemTypeViewModel.updateItemQuantity(id, Number(value));
    };

    const handleFill = () => {
        itemTypeViewModel.fillItemType(id, itemType);
    };

    const handleClear = () => {
        itemTypeViewModel.updateItemQuantity(id, 0);
    };

    return (
        <div className={css.item}>
            <div
                className={classNames(css.imgAndTitle, {
                    [css.clickable]: customProject.storeId,
                })}
                onClick={handleShowItem}
            >
                <img className={css.img} src={imgUrl} alt="item-image" />
                <div className={css.titleAndUnitPrice}>
                    <div>{item.title}</div>
                    <div className={css.price}>
                        <span>R {FormatNumber(item.price, '0.00', 2)}</span>
                    </div>
                </div>
            </div>

            <div className={css.quantity}>
                {GetPackagingText(packaging) === ''
                    ? item.unit
                    : GetPackagingText(packaging)}
            </div>

            <div
                className={css.quantityInputContainer}
                id={
                    SelectItemsModalElementsIdsEnum.SELECT_MODAL_STEP2_INPUT_FIELD
                }
            >
                <CustomNumberInput
                    value={quantity}
                    increase={handleIncreaseQuantity}
                    decrease={handleDecreaseQuantity}
                    onChange={handleChangeQuantity}
                    disabled={isDisabled}
                    controlsClassName={css.changeQuantity}
                    decrementBtnClassName={css.decrement}
                    incrementBtnClassName={css.increment}
                    decrementDisabled={isDecreaseDisabled}
                    incrementDisabled={isIncreaseDisabled}
                />
            </div>

            <div className={css.fillBtnContainer}>
                {quantity > 0 ? (
                    <BasicButton
                        onClick={handleClear}
                        extraClassName={css.fillBtn}
                    >
                        Clear
                    </BasicButton>
                ) : (
                    <BasicButton
                        onClick={handleFill}
                        extraClassName={css.fillBtn}
                        disabled={isFillDisabled || isDisabled}
                    >
                        Fill
                    </BasicButton>
                )}
            </div>

            <ItemAvailability qtyFromStore={qtyFromStore} />
        </div>
    );
};
